import { Resource, ResourceType, Unit, UnitType } from "types";
import { useSeller } from "../useAccount";
import { useAutoFetchCollection } from "../useAutoFetchCollection";

export interface EquipmentResourceWithTemplate {
    id: string;
    name: string;
    templateId: string;
    capacity: number;
}

/**
 * Returns equipment resources paired with their unit template ID and capacity.
 * Resources carry a `unit` reference (set at creation via createUnitForEquipmentResource).
 * Units are fetched to resolve unit.id → lineItemTemplate.id.
 */
export const useEquipmentResourcesWithTemplates = (): EquipmentResourceWithTemplate[] => {
    const seller = useSeller();
    const sellerId = seller?.id;

    const { data: resources } = useAutoFetchCollection<Resource>(
        "resources",
        { seller: sellerId },
        { enabled: !!sellerId },
    );

    const { data: units } = useAutoFetchCollection<Unit>(
        "units",
        { type: UnitType.EQUIPMENT, seller: sellerId },
        { enabled: !!sellerId },
    );

    const unitTemplateMap = new Map<string, string>();
    for (const unit of units) {
        if (unit.id && unit.lineItemTemplate?.id) {
            unitTemplateMap.set(unit.id, unit.lineItemTemplate.id);
        }
    }

    return resources
        .filter(
            (resource) =>
                resource.type === ResourceType.EQUIPMENT && resource.unit?.id && unitTemplateMap.has(resource.unit.id),
        )
        .map((resource) => ({
            id: resource.id,
            name: resource.name,
            templateId: unitTemplateMap.get(resource.unit!.id!)!,
            capacity: resource.capacity,
        }));
};
